import type { CSSProperties } from "react";

export type Breakpoint = "desktop" | "tablet" | "mobile";

export type SpacingValue = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  linked?: boolean;
};

export type StyleProps = {
  padding?: SpacingValue;
  margin?: SpacingValue;
  backgroundColor?: string;
  backgroundImage?: string;
  textAlign?: "left" | "center" | "right";
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  boxShadow?: string;
  customClass?: string;
  customCss?: string;
};

export type ResponsiveStyleProps = {
  desktop: StyleProps;
  tablet?: Partial<StyleProps>;
  mobile?: Partial<StyleProps>;
};

export const defaultStyleProps = (): ResponsiveStyleProps => ({
  desktop: {},
});

export function emptySpacing(): SpacingValue {
  return { top: 0, right: 0, bottom: 0, left: 0, linked: true };
}

function spacingToCss(spacing?: SpacingValue): string | undefined {
  if (!spacing) return undefined;
  const t = spacing.top ?? 0;
  const r = spacing.right ?? t;
  const b = spacing.bottom ?? t;
  const l = spacing.left ?? r;
  if (t === 0 && r === 0 && b === 0 && l === 0) return undefined;
  return `${t}px ${r}px ${b}px ${l}px`;
}

export function mergeStyleForBreakpoint(
  responsive: ResponsiveStyleProps | undefined,
  breakpoint: Breakpoint = "desktop",
): StyleProps {
  if (!responsive) return {};
  const base = responsive.desktop ?? {};
  if (breakpoint === "desktop") return base;
  const override = breakpoint === "tablet" ? responsive.tablet : responsive.mobile;
  return { ...base, ...override };
}

export function applyStyleProps(
  responsive: ResponsiveStyleProps | undefined,
  breakpoint: Breakpoint = "desktop",
  nodeId?: string,
): { style: CSSProperties; className: string } {
  const s = mergeStyleForBreakpoint(responsive, breakpoint);
  const style: CSSProperties = {};

  const padding = spacingToCss(s.padding);
  if (padding) style.padding = padding;
  const margin = spacingToCss(s.margin);
  if (margin) style.margin = margin;
  if (s.backgroundColor) style.backgroundColor = s.backgroundColor;
  if (s.backgroundImage) {
    style.backgroundImage = `url(${s.backgroundImage})`;
    style.backgroundSize = "cover";
    style.backgroundPosition = "center";
  }
  if (s.textAlign) style.textAlign = s.textAlign;
  if (s.fontSize) style.fontSize = `${s.fontSize}px`;
  if (s.fontWeight) style.fontWeight = s.fontWeight;
  if (s.color) style.color = s.color;
  if (s.borderRadius != null) style.borderRadius = `${s.borderRadius}px`;
  if (s.borderWidth != null) {
    style.borderWidth = `${s.borderWidth}px`;
    style.borderStyle = "solid";
    style.borderColor = s.borderColor ?? "currentColor";
  }
  if (s.boxShadow) style.boxShadow = s.boxShadow;

  const classes = [s.customClass, nodeId ? `craft-node-${nodeId}` : ""].filter(Boolean).join(" ");
  return { style, className: classes };
}

export function buildResponsiveCss(nodeId: string, responsive: ResponsiveStyleProps): string {
  const rules: string[] = [];
  const sel = `.craft-node-${nodeId}`;

  const desktop = stylePropsToCssRules(mergeStyleForBreakpoint(responsive, "desktop"));
  if (desktop) rules.push(`${sel} { ${desktop} }`);

  const tablet = stylePropsToCssRules(mergeStyleForBreakpoint(responsive, "tablet"));
  if (tablet) rules.push(`@media (max-width: 1024px) { ${sel} { ${tablet} } }`);

  const mobile = stylePropsToCssRules(mergeStyleForBreakpoint(responsive, "mobile"));
  if (mobile) rules.push(`@media (max-width: 640px) { ${sel} { ${mobile} } }`);

  const custom = [
    responsive.desktop.customCss,
    responsive.tablet?.customCss,
    responsive.mobile?.customCss,
  ]
    .filter(Boolean)
    .join("\n");
  if (custom) rules.push(custom);

  return rules.join("\n");
}

function stylePropsToCssRules(s: StyleProps): string | null {
  const parts: string[] = [];
  const padding = spacingToCss(s.padding);
  if (padding) parts.push(`padding: ${padding}`);
  const margin = spacingToCss(s.margin);
  if (margin) parts.push(`margin: ${margin}`);
  if (s.backgroundColor) parts.push(`background-color: ${s.backgroundColor}`);
  if (s.textAlign) parts.push(`text-align: ${s.textAlign}`);
  if (s.fontSize) parts.push(`font-size: ${s.fontSize}px`);
  if (s.fontWeight) parts.push(`font-weight: ${s.fontWeight}`);
  if (s.color) parts.push(`color: ${s.color}`);
  if (s.borderRadius != null) parts.push(`border-radius: ${s.borderRadius}px`);
  return parts.length ? parts.join("; ") : null;
}

export const CRAFT_STATE_MAX_BYTES = 2 * 1024 * 1024;

export function validateCraftStateSize(serialized: string): { ok: boolean; error?: string } {
  const bytes = new TextEncoder().encode(serialized).length;
  if (bytes > CRAFT_STATE_MAX_BYTES) {
    return { ok: false, error: `Craft content too large (${Math.round(bytes / 1024)}KB). Max 2MB.` };
  }
  return { ok: true };
}
